import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef,
} from 'react';
import { AutoSizer, List } from 'react-virtualized';
import { Box, Text, Spinner } from '@usealloy/component-library';
import styled from 'styled-components';
import {
  hideNativeScrollbar,
  OverlayScrollbar,
  OverlayScrollWrapper,
  useOverlayScrollbar,
} from '../../components/OverlayScrollbar';
import type { ScrollMetrics } from '../../components/OverlayScrollbar';
import { activateCustomerRow, CustomerRow } from './CustomerRow';
import { useAllCustomersData } from './useAllCustomersData';
import {
  ALL_CUSTOMERS_MAX_LIST_HEIGHT,
  ALL_CUSTOMERS_POPOVER_MIN_WIDTH,
  ALL_CUSTOMERS_ROW_HEIGHT,
} from './constants';
import type { AllCustomersRow } from './types';

const SectionLabel = styled(Text)({
  padding: '8px 12px 4px',
  color: '#646461',
});

/**
 * The virtualized list plus its overlay scrollbar, matching the sidebar's own
 * scroll affordance (see `components/OverlayScrollbar`).
 *
 * Full width so `AutoSizer` below measures the popover rather than a hardcoded
 * row width — the rows then track the popover even when its content grows it
 * past `ALL_CUSTOMERS_POPOVER_MIN_WIDTH`.
 *
 * `react-virtualized`'s `Grid` owns the element that actually scrolls, so the
 * native scrollbar is suppressed on that (a grandchild, under `AutoSizer`'s own
 * div) rather than here. The overlay thumb then floats over the rows instead of
 * eating into the width they are laid out against.
 */
const ListWrapper = styled(OverlayScrollWrapper)({
  width: '100%',

  '.ReactVirtualized__Grid': hideNativeScrollbar,
});

// Box already centers content by default (alignItems/justifyContent="center")
const EmptyStateBox = styled(Box)({
  minHeight: '64px',
});

/**
 * A higher-order function for rendering a customer row in the virtualized
 * list — mirrors the `getSubMenuRowRenderer` pattern in
 * `journey/nodes/AddNodeFormNode/.../SubMenu/index.tsx`, react-virtualized's
 * own precedent elsewhere in Daffy for a searchable, virtualized dropdown.
 */
function getCustomerRowRenderer(
  rows: AllCustomersRow[],
  currentCellKey: string,
  onRequestAccess: (row: AllCustomersRow) => void,
  highlightedIndex: number,
  onRowHover: (index: number) => void,
) {
  return function RowRenderer({ key, style, index }: { key: any; style: any; index: number }) {
    return (
      <CustomerRow
        key={key}
        style={style}
        row={rows[index]}
        currentCellKey={currentCellKey}
        onRequestAccess={onRequestAccess}
        isHighlighted={index === highlightedIndex}
        onHover={() => onRowHover(index)}
      />
    );
  };
}

export interface AllCustomersSectionHandle {
  /** Activate (switch/request-access) the row at the given index within the current filtered list, as if it were clicked. */
  activateRow: (index: number) => void;
}

interface AllCustomersSectionProps {
  /** Lazy-fetch gate — only true once the switcher popover is actually open */
  enabled: boolean;
  /** `alloyEnvironment.replace('kube', '')` — needed to route same-cell vs cross-cell nav */
  currentCellKey: string;
  onRequestAccess: (row: AllCustomersRow) => void;
  /** Shared with "My Accounts"/"Current" — the switcher's single top-level search input */
  searchQuery: string;
  /** Index (within the current filtered rows) of the keyboard-highlighted row, or -1 for none */
  highlightedIndex: number;
  /** Called whenever the filtered row count changes, so the parent's combined keyboard-nav index can include this section */
  onCountChange: (count: number) => void;
  /**
   * Called with the index (within the current filtered rows) of the row the
   * pointer entered, so the parent's shared highlight follows the mouse the way
   * it already does over "My Accounts" and the "Customer List" footer.
   */
  onRowHover: (index: number) => void;
}

/**
 * Admin-only "All Customers" section: every customer account (not just the
 * ones the agent already has access to), filtered by the switcher's existing
 * top-level search input (no separate search box), rendered as a virtualized
 * list since the full set can be 500+ orgs.
 */
export const AllCustomersSection = forwardRef<AllCustomersSectionHandle, AllCustomersSectionProps>((
  {
    enabled,
    currentCellKey,
    onRequestAccess,
    searchQuery,
    highlightedIndex,
    onCountChange,
    onRowHover,
  },
  ref,
) => {
  const { rows, isLoading, isError } = useAllCustomersData(enabled);
  const listRef = useRef<List>(null);
  const scrollTopRef = useRef(0);
  const {
    isVisible, syncMetrics, thumb, wrapperProps,
  } = useOverlayScrollbar();

  const filteredRows = useMemo(() => rows.filter(row => (
    searchQuery
      ? row.organization_name?.toLowerCase().includes(searchQuery.toLowerCase())
      : true
  )), [rows, searchQuery]);

  useEffect(() => {
    onCountChange(filteredRows.length);
  }, [filteredRows.length, onCountChange]);

  useEffect(() => {
    if (highlightedIndex >= 0) {
      listRef.current?.scrollToRow(highlightedIndex);
    }
  }, [highlightedIndex]);

  useImperativeHandle(ref, () => ({
    activateRow: index => {
      const row = filteredRows[index];
      if (row) activateCustomerRow(row, { currentCellKey, onRequestAccess });
    },
  }), [filteredRows, currentCellKey, onRequestAccess]);

  const contentHeight = filteredRows.length * ALL_CUSTOMERS_ROW_HEIGHT;
  const listHeight = Math.min(contentHeight, ALL_CUSTOMERS_MAX_LIST_HEIGHT);

  const handleListScroll = useCallback((metrics: ScrollMetrics) => {
    scrollTopRef.current = metrics.scrollTop;
    syncMetrics(metrics);
  }, [syncMetrics]);

  /**
   * Seed the thumb's geometry from the dimensions we already know.
   *
   * The hook's `ResizeObserver` path is inactive here — `react-virtualized` owns
   * the viewport — so without this, hovering a list that hasn't been scrolled
   * yet would show no scrollbar at all. Re-runs when filtering changes the
   * list's size, clamping the last known scroll offset the way `Grid` does.
   */
  useEffect(() => {
    syncMetrics({
      clientHeight: listHeight,
      scrollHeight: contentHeight,
      scrollTop: Math.min(scrollTopRef.current, Math.max(contentHeight - listHeight, 0)),
    }, { reveal: false });
  }, [contentHeight, listHeight, syncMetrics]);

  if (!enabled) return null;

  return (
    /*
     * `data-overflowing` is published on the section root, not just on the
     * scroll wrapper inside it, so the switcher's "Customer List" footer can
     * cast its overflow shadow over this list the same way `Sidebar.Footer`
     * does over `Sidebar.Body` (see NavList.customerSwitcher).
     */
    <Box
      data-component="CustomerSwitcher.AllCustomers"
      data-overflowing={!!thumb}
      direction="column"
      width="100%"
      alignItems="start"
    >
      <SectionLabel size={1}>All Customers</SectionLabel>

      {isLoading && (
        <EmptyStateBox>
          <Spinner size="small" style={{ marginRight: '8px' }} />
          <Text size={2} style={{ color: 'var(--cl-gray-800)' }}>Loading customers...</Text>
        </EmptyStateBox>
      )}

      {!isLoading && isError && (
        <EmptyStateBox>
          <Text size={2} style={{ color: 'var(--cl-gray-800)' }}>Failed to load customers</Text>
        </EmptyStateBox>
      )}

      {!isLoading && !isError && filteredRows.length === 0 && (
        <EmptyStateBox>
          <Text size={2} style={{ color: 'var(--cl-gray-800)' }}>No customers found</Text>
        </EmptyStateBox>
      )}

      {!isLoading && !isError && filteredRows.length > 0 && (
        <ListWrapper {...wrapperProps}>
          <AutoSizer disableHeight>
            {({ width }) => (
              <List
                ref={listRef}
                height={listHeight}
                rowCount={filteredRows.length}
                rowHeight={ALL_CUSTOMERS_ROW_HEIGHT}
                // `Grid` renders no cells at all for a zero width, which is what
                // AutoSizer reports before its first measurement (and in jsdom,
                // which has no layout) — so never go below the popover's floor.
                width={Math.max(width, ALL_CUSTOMERS_POPOVER_MIN_WIDTH)}
                onScroll={handleListScroll}
                rowRenderer={getCustomerRowRenderer(
                  filteredRows, currentCellKey, onRequestAccess, highlightedIndex, onRowHover,
                )}
              />
            )}
          </AutoSizer>
          <OverlayScrollbar isVisible={isVisible} thumb={thumb} />
        </ListWrapper>
      )}
    </Box>
  );
});

AllCustomersSection.displayName = 'AllCustomersSection';
